/**
 * LanguageSelector - dropdown cu 6 limbi (ro, en, fr, it, de, es).
 *
 * Variante:
 *  - mode="login": aplicare imediata pe i18n + persistare in localStorage, fara backend.
 *    Folosit inainte de autentificare.
 *  - mode="profile": aplicare imediata pe i18n, salvare server-side (PUT /api/auth/me/language),
 *    apoi confirmare + forced logout (cerinta user: schimbarea limbii necesita relogare).
 *
 * Text afisat in dropdown fara abreviere (cerinta user).
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n, { SUPPORTED_LANGS } from '../../i18n';
import { API_URL } from '../../utils/api';
import { Globe } from 'lucide-react';

const LanguageSelector = ({ mode = 'login', onChangeStart, onChangeDone, className = '', dropdownDirection = 'down' }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [pendingLang, setPendingLang] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const currentLang = i18n.resolvedLanguage || i18n.language || 'en';
  const currentMeta = SUPPORTED_LANGS.find(l => l.code === currentLang) || SUPPORTED_LANGS[1];

  const applyLoginMode = (code) => {
    i18n.changeLanguage(code);
    try { localStorage.setItem('oferte_lang', code); } catch (e) { /* ignore */ }
    setOpen(false);
  };

  const applyProfileMode = async (code) => {
    setError('');
    setBusy(true);
    if (onChangeStart) onChangeStart();
    try {
      const token = localStorage.getItem('oferte_token');
      const res = await fetch(`${API_URL}/api/auth/me/language`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ lang: code }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || t('language.change_error'));
      }
      i18n.changeLanguage(code);
      try { localStorage.setItem('oferte_lang', code); } catch (e) { /* ignore */ }
      // Sesiune 31 iter 14: aplicare imediata, fara forced logout. user a cerut.
      // Actualizam si user-ul stocat in localStorage cu noua limba.
      try {
        const userStr = localStorage.getItem('oferte_user');
        if (userStr) {
          const u = JSON.parse(userStr);
          u.lang = code;
          localStorage.setItem('oferte_user', JSON.stringify(u));
        }
      } catch (e) { /* ignore */ }
      if (onChangeDone) onChangeDone(code);
    } catch (e) {
      setError(e.message || t('language.change_error'));
    } finally {
      setBusy(false);
      setPendingLang(null);
    }
  };

  const handleSelect = (code) => {
    if (code === currentLang) { setOpen(false); return; }
    if (mode === 'login') {
      applyLoginMode(code);
      return;
    }
    // Profile mode: aplica direct, fara modal de confirmare (user a cerut sesiune 31 iter 14).
    setOpen(false);
    applyProfileMode(code);
  };

  return (
    <div className={`relative ${className}`} data-testid="lang-selector-root">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        disabled={busy}
        className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-slate-300 bg-white text-sm text-slate-700 hover:bg-slate-50"
        data-testid="lang-selector-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <Globe className="h-4 w-4 text-slate-500" />
        <span>{currentMeta.name}</span>
      </button>
      {open && (
        <ul
          className={`absolute right-0 ${dropdownDirection === 'up' ? 'bottom-full mb-1' : 'mt-1 top-full'} min-w-[160px] bg-white border border-slate-200 rounded-md shadow-lg z-[200]`}
          data-testid="lang-selector-list"
          role="listbox"
        >
          {SUPPORTED_LANGS.map(l => (
            <li key={l.code}>
              <button
                type="button"
                onClick={() => handleSelect(l.code)}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 ${l.code === currentLang ? 'font-semibold text-blue-700' : 'text-slate-700'}`}
                data-testid={`lang-option-${l.code}`}
                role="option"
                aria-selected={l.code === currentLang}
              >
                {l.name}
              </button>
            </li>
          ))}
        </ul>
      )}
      {mode === 'profile' && pendingLang && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full p-5 space-y-4" data-testid="lang-confirm-modal">
            <h3 className="font-bold text-base">{t('language.change_title')}</h3>
            <p className="text-sm text-slate-600">{t('language.change_warning')}</p>
            {error && <p className="text-sm text-red-600" data-testid="lang-confirm-error">{error}</p>}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => { setPendingLang(null); setError(''); }}
                disabled={busy}
                className="px-3 py-1.5 rounded-md border border-slate-300 text-sm"
                data-testid="lang-confirm-cancel"
              >
                {t('language.change_cancel')}
              </button>
              <button
                type="button"
                onClick={() => applyProfileMode(pendingLang)}
                disabled={busy}
                className="px-3 py-1.5 rounded-md bg-blue-600 text-white text-sm hover:bg-blue-700 disabled:opacity-50"
                data-testid="lang-confirm-apply"
              >
                {busy ? '...' : t('language.change_confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
